import { KeyboardEvent, MouseEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import styled from '@emotion/styled';
import { Button, InputAdornment, Menu, MenuItem, TextField } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { applyFilter } from '@/redux/slices/dataTableSlice';
import { selectCurrentTableKey, selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import { FilterBehavior } from '@/components/_common/Filters/Filter';
import { TooltipOverflow } from '@/exports/columnCommonComponents';
import PolarisIcon from '@/components/_common/PolarisIcon';
import DropdownSelect from '@/components/_common/Form/DropdownSelect';
import FilterChip from '@/components/_common/Filters/FilterChip';
import FilterAccordion from '@/components/_common/Filters/FilterAccordion';

export enum FilterIDType {
  ORDER_ID = 'orders',
  JOB_ID = 'jobs',
  TASK_ID = 'tasks',
  ASSET_ID = 'pendingAssets',
  PROCESSING_TASKS = 'processingTasks',
  JOB_BUNDLE_ID = 'job_bundle',
  EXCEPTION_ID = 'exceptions',
  RELATED_ID = 'reference',
  VERSION_RLID = 'version_rlid',
  VERSION_P7ID = 'version_p7id',
  VERSION_MCID = 'version_mcid',
  RLID = 'rlid',
  P7ID = 'p7id',
  MCID = 'mcid'
}

type FilterIDProps = {
  label: string;
  options?: () => FilterIDType[];
  excludeOptions?: () => Record<string, string>;
  type: string;
  role?: ReturnType<typeof createSelector>;
  isQuickFilters: boolean;
  behavior?: FilterBehavior;
  expandedFilters?: string[];
  onExpand?: (filter: string) => void;
  advanceFilters?: Record<string, any>;
  onAdvanceFiltersChange?: (newAdvanceFilters: { type: string; selections: any }) => void;
};

export const FilterKeyMap = {
  [FilterIDType.ORDER_ID]: 'order_hid_filter',
  [FilterIDType.JOB_ID]: 'job_hid_filter',
  [FilterIDType.TASK_ID]: 'task_hid_filter',
  [FilterIDType.PROCESSING_TASKS]: 'task_hid_filter',
  [FilterIDType.JOB_BUNDLE_ID]: 'bundle_job_hid_filter',
  [FilterIDType.ASSET_ID]: 'job_asset_hid_filter',
  [FilterIDType.EXCEPTION_ID]: 'exception_hid_filter',
  [FilterIDType.RELATED_ID]: 'reference_id_filter',
  [FilterIDType.VERSION_RLID]: 'version_rightsline_id_filter',
  [FilterIDType.VERSION_P7ID]: 'version_p7_id_filter',
  [FilterIDType.VERSION_MCID]: 'version_mcid_filter',
  [FilterIDType.RLID]: 'rightsline_id_filter',
  [FilterIDType.P7ID]: 'p7_id_filter',
  [FilterIDType.MCID]: 'mcid_filter'
};

const FilterLabelMap = {
  [FilterIDType.ORDER_ID]: 'Order ID',
  [FilterIDType.JOB_ID]: 'Job ID',
  [FilterIDType.TASK_ID]: 'Task ID',
  [FilterIDType.PROCESSING_TASKS]: 'Task ID',
  [FilterIDType.ASSET_ID]: 'Asset ID',
  [FilterIDType.JOB_BUNDLE_ID]: 'Job Bundle',
  [FilterIDType.EXCEPTION_ID]: 'Exception ID',
  [FilterIDType.RELATED_ID]: 'Related ID',
  [FilterIDType.VERSION_RLID]: 'Version RLID',
  [FilterIDType.VERSION_P7ID]: 'Version P7 ID',
  [FilterIDType.VERSION_MCID]: 'Version MCID',
  [FilterIDType.RLID]: 'RLID',
  [FilterIDType.P7ID]: 'P7 ID',
  [FilterIDType.MCID]: 'MCID'
};

const FilterID = ({
  behavior,
  label,
  options,
  excludeOptions,
  type,
  role,
  isQuickFilters,
  expandedFilters = [],
  onExpand,
  advanceFilters,
  onAdvanceFiltersChange
}: FilterIDProps) => {
  const isFiltersV2 = useContext(ConfigContext)?.filtersVersion === 2;
  const hasPermissions = role ? useAppSelector(role) : true;
  const searchBoxRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const currentTable = useAppSelector(selectCurrentTableKey);
  const filters = useAppSelector(selectDataTableFilters);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [table, setTable] = useState<FilterIDType>(FilterIDType.ORDER_ID);
  const [value, setValue] = useState('');
  const isAccordionExpanded = expandedFilters.includes(type);
  const isAdvanceFilter = isFiltersV2 && !isQuickFilters;
  const filtersToUse = isAdvanceFilter && advanceFilters ? advanceFilters : filters;

  const filterField = useMemo(() => {
    return FilterKeyMap[table] || FilterKeyMap[currentTable as FilterIDType];
  }, [table, currentTable, FilterKeyMap]);

  const multiIDs = useMemo(
    () => (filtersToUse[type] && typeof filtersToUse[type][0] !== 'string' ? filtersToUse[type][0] : false),
    [filtersToUse, type]
  );

  const filterValue: string = useMemo(() => {
    if (filtersToUse[type] && typeof filtersToUse[type][0] !== 'string') return [];
    return filtersToUse[type] || [];
  }, [filtersToUse, type]);

  const actualFilteredField: FilterIDType = useMemo(() => {
    const filter = filtersToUse[type];

    if (filter && filter.length > 1) {
      const filterKey = filter[1];
      const filterIDType = Object.values(FilterIDType).find((type) => FilterKeyMap[type] === filterKey);

      if (filterIDType) {
        return filterIDType as FilterIDType;
      }
    }

    return (behavior?.defaultOption ?? currentTable) as FilterIDType;
  }, [type, filtersToUse, table, behavior?.defaultOption]);

  const filterOptions = useMemo(() => {
    let defaultList = [
      FilterIDType.ORDER_ID,
      FilterIDType.JOB_ID,
      FilterIDType.ASSET_ID,
      FilterIDType.JOB_BUNDLE_ID,
      table === 'processingTasks' ? FilterIDType.PROCESSING_TASKS : FilterIDType.TASK_ID
    ];

    if (options) {
      return options();
    }

    if (currentTable === 'pendingAssets') {
      defaultList = defaultList.filter((item) => item !== FilterIDType.TASK_ID);
    }

    if (currentTable === 'processingTasks') {
      defaultList = defaultList.filter((item) => item !== FilterIDType.ASSET_ID);
    }

    if (currentTable === 'exceptions') {
      defaultList = [FilterIDType.EXCEPTION_ID, FilterIDType.RELATED_ID];
    }

    if (excludeOptions) {
      defaultList = defaultList.filter((key) => !Object.values(excludeOptions()).includes(key));
    }

    return defaultList;
  }, [excludeOptions, options, table, currentTable]);

  useEffect(() => {
    if (filterValue.length) {
      setValue(getAdornment() !== '' ? filterValue[0].substring(1) : filterValue[0]);
    } else {
      setValue('');
    }
  }, [filterValue]);

  useEffect(() => {
    setTable(actualFilteredField);
  }, [actualFilteredField]);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => setAnchorEl(event.currentTarget);

  const handleClose = () => {
    setAnchorEl(null);
    if (filterValue.length) {
      setTable(actualFilteredField);
      setValue(getAdornment() !== '' ? filterValue[0].substring(1) : filterValue[0]);
    } else {
      setValue('');
      setTable((behavior?.defaultOption ?? currentTable) as FilterIDType);
    }
  };

  const handleChangeTable = (change: FilterIDType) => {
    setTable(change);
    setValue('');
    setTimeout(() => {
      searchBoxRef?.current?.focus();
    });
  };

  const handleClear = (e: any) => {
    e.stopPropagation();
    setValue('');
    const newValue = { type, selections: [] };
    if (isAdvanceFilter && onAdvanceFiltersChange) {
      onAdvanceFiltersChange(newValue);
    } else {
      dispatch(applyFilter(newValue));
    }
  };

  const getAdornment = () => {
    if (behavior?.noAdornment) {
      return '';
    }

    switch (table) {
      case FilterIDType.ORDER_ID:
        return 'W';
      case FilterIDType.JOB_ID:
        return 'J';
      case FilterIDType.PROCESSING_TASKS:
      case FilterIDType.TASK_ID:
        return 'T';
      case FilterIDType.ASSET_ID:
        return 'A';
      case FilterIDType.EXCEPTION_ID:
        return 'X';
      case FilterIDType.JOB_BUNDLE_ID:
        return 'B';
      default:
        return '';
    }
  };

  const applyTextFilter = () => {
    const valueWithAdornment = `${getAdornment()}${value}`;
    const newValue = { type, selections: [valueWithAdornment, filterField] };
    if (isAdvanceFilter && onAdvanceFiltersChange) {
      onAdvanceFiltersChange(newValue);
    } else {
      dispatch(applyFilter(newValue));
    }
    setAnchorEl(null);
  };

  const onWrapperKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      handleClose();
    }
    if (e.key === 'Enter') {
      if (value.trim() === '') {
        const newValue = { type, selections: [] };
        if (isAdvanceFilter && onAdvanceFiltersChange) {
          onAdvanceFiltersChange(newValue);
        } else {
          dispatch(applyFilter(newValue));
        }
      } else {
        applyTextFilter();
      }
      setAnchorEl(null);
    }
  };

  if (!hasPermissions) return null;

  return isFiltersV2 && !isQuickFilters ? (
    <FilterAccordion
      expanded={isAccordionExpanded}
      label={label}
      showClear={filterValue?.length > 0 || !!multiIDs}
      focusElementOnExpand={searchBoxRef}
      onClear={handleClear}
      onChange={() => onExpand?.(type)}
    >
      <Container onKeyDown={onWrapperKeyDown} style={{ padding: 0 }}>
        <DropdownSelect label="Type" name="date_format" value={table} onChange={handleChangeTable}>
          {filterOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {FilterLabelMap[option]}
            </MenuItem>
          ))}
        </DropdownSelect>
        <SearchBox
          inputRef={searchBoxRef}
          autoFocus
          type={behavior?.filterInputType ?? 'number'}
          label={label}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ opacity: 0.75, marginRight: 0.25 }}>
                {getAdornment()}
              </InputAdornment>
            ),
            endAdornment: (
              <EndAdornmentIcon
                visibility={value.trim().length > 0 ? 'visible' : 'hidden'}
                type={'close'}
                icon={'close_filled'}
                onMouseDown={() => setValue('')}
              />
            )
          }}
        />
        <ApplyButton disabled={value.length <= 0} variant="text" color="primary" onClick={applyTextFilter} sx={{ ml: 1 }}>
          Apply
        </ApplyButton>
      </Container>
    </FilterAccordion>
  ) : (
    <div style={{ marginRight: 10 }}>
      <FilterChip
        label={
          <>
            {filterValue.length ? FilterLabelMap[actualFilteredField] : label}:{' '}
            <ValueDisplay>{multiIDs ? <TooltipOverflow length={1} items={multiIDs} /> : filterValue.length ? filterValue[0] : 'All'}</ValueDisplay>
          </>
        }
        isSelected={!!filterValue.length || !!multiIDs}
        onClick={handleClick as any}
        onClear={handleClear as any}
      />

      <Menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        variant="menu"
      >
        <Container onKeyDown={onWrapperKeyDown}>
          <DropdownSelect label="Type" name="date_format" value={table} onChange={handleChangeTable}>
            {filterOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {FilterLabelMap[option]}
              </MenuItem>
            ))}
          </DropdownSelect>
          <SearchBox
            inputRef={searchBoxRef}
            autoFocus
            type={behavior?.filterInputType ?? 'number'}
            label={label}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ opacity: 0.75, marginRight: 0.25 }}>
                  {getAdornment()}
                </InputAdornment>
              ),
              endAdornment: (
                <EndAdornmentIcon
                  visibility={value.trim().length > 0 ? 'visible' : 'hidden'}
                  type={'close'}
                  icon={'close_filled'}
                  onMouseDown={() => setValue('')}
                />
              )
            }}
          />
          <ApplyButton disabled={value.length <= 0} variant="text" color="primary" onClick={applyTextFilter} sx={{ ml: 1 }}>
            Apply
          </ApplyButton>
        </Container>
      </Menu>
    </div>
  );
};

export default FilterID;

const Container = styled.div`
  display: flex;
  align-items: center;
  padding: ${(props) => props.theme.spacing(2)};
`;

const SearchBox = styled(TextField)`
  && {
    margin-left: ${(props) => props.theme.spacing(2)};
    width: 130px;

    & .MuiInputBase-root {
      padding-right: 0;
      font-size: 1rem;
    }

    & .MuiInputLabel-root {
      font-size: 1rem;
    }
  }
`;

const ValueDisplay = styled.strong`
  margin-left: ${(props) => props.theme.spacing(0.5)};
`;

const EndAdornmentIcon = styled(PolarisIcon)<{ type: 'search' | 'close' }>`
  cursor: pointer;
  color: inherit;
  transition: ease opacity 200ms;
  width: 25px;
  height: 25px;
  margin-right: ${(props) => props.theme.spacing(1)};
  opacity: 0.3;

  &:hover {
    opacity: 0.5;
    transition: ease opacity 200ms;
  }
`;

const ApplyButton = styled(Button)`
  &.Mui-disabled {
    opacity: 0;
  }
`;
